// 非相機裝置設定（APP#39）：Plug 插座 0x8001 / Sensor 環境感測 0x8010。
// 兩簇前此列為 KNOWN_BUT_UNRENDERED（web-v1 未做，Plug 因 GAP-DS-2「成員表缺」）；
// dictionary v1.6.0 已補齊成員 → 本檔補為 capability-driven section。
//   - member id 全取自 as-built capability_dictionary.json（spec-ssot-discipline，never invent）。
//   - STAT（功率/電壓/電流/電量/溫濕度）唯讀顯示；SET（OutletOn/溫度上下限）走 useAttr 樂觀 op:set。
//   - 濕度上下限 attr 不存在（GAP-DS-3）→ 濕度唯讀、不渲染閾值 widget。

import { useTranslation } from 'react-i18next';
import {
  PLUG_ACTIVE_POWER,
  PLUG_CURRENT,
  PLUG_ENERGY,
  PLUG_OUTLET_ON,
  PLUG_VOLTAGE,
  SENSOR_HUMIDITY,
  SENSOR_TEMPERATURE,
  SENSOR_TEMP_LOWER,
  SENSOR_TEMP_UPPER,
} from './ids';
import { useAttr } from './ops';
import { ReadonlyRow, SectionCard, SliderRow, ToggleRow } from './widgets';

function Connecting({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <SectionCard title={title}>
      <p className="text-secondary" style={{ margin: 0 }}>
        {t('deviceSettings.connecting')}
      </p>
    </SectionCard>
  );
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined;
}

/** 插座（0x8001）：通斷開關 + 即時電量量測（唯讀）。 */
export function PlugSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const on = useAttr(deviceId, PLUG_OUTLET_ON, wsOpen);
  const power = useAttr(deviceId, PLUG_ACTIVE_POWER, wsOpen);
  const voltage = useAttr(deviceId, PLUG_VOLTAGE, wsOpen);
  const current = useAttr(deviceId, PLUG_CURRENT, wsOpen);
  const energy = useAttr(deviceId, PLUG_ENERGY, wsOpen);

  if (!wsOpen) return <Connecting title={t('deviceSettings.plug')} />;

  return (
    <SectionCard title={t('deviceSettings.plug')}>
      <ToggleRow label={t('deviceSettings.outletOn')} attr={on} />
      {/* 電量量測 STAT（dictionary 標度：功率/電壓 0.1/LSB、電流 1 mA/LSB、電量 1 Wh/LSB） */}
      <ReadonlyRow label={t('deviceSettings.activePower')} attr={power} render={(v) => `${((num(v) ?? 0) / 10).toFixed(1)} W`} />
      <ReadonlyRow label={t('deviceSettings.voltage')} attr={voltage} render={(v) => `${((num(v) ?? 0) / 10).toFixed(1)} V`} />
      <ReadonlyRow label={t('deviceSettings.current')} attr={current} render={(v) => `${((num(v) ?? 0) / 1000).toFixed(3)} A`} />
      <ReadonlyRow label={t('deviceSettings.energy')} attr={energy} render={(v) => `${((num(v) ?? 0) / 1000).toFixed(3)} kWh`} />
    </SectionCard>
  );
}

/** 環境感測（0x8010）：溫濕度（唯讀）+ 溫度上下限（SET）。濕度閾值 GAP-DS-3 不渲染。 */
export function SensorSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const temp = useAttr(deviceId, SENSOR_TEMPERATURE, wsOpen);
  const humidity = useAttr(deviceId, SENSOR_HUMIDITY, wsOpen);
  const upper = useAttr(deviceId, SENSOR_TEMP_UPPER, wsOpen);
  const lower = useAttr(deviceId, SENSOR_TEMP_LOWER, wsOpen);

  if (!wsOpen) return <Connecting title={t('deviceSettings.sensor')} />;

  return (
    <SectionCard title={t('deviceSettings.sensor')}>
      <ReadonlyRow label={t('deviceSettings.temperature')} attr={temp} render={(v) => `${num(v) ?? '—'} °C`} />
      <ReadonlyRow label={t('deviceSettings.humidity')} attr={humidity} render={(v) => `${num(v) ?? '—'} %`} />
      {/* 溫度上/下限 SET（dictionary 無 range → 採合理 -40~80 °C 過渡值） */}
      <SliderRow label={t('deviceSettings.tempUpper')} attr={upper} min={-40} max={80} unit=" °C" />
      <SliderRow label={t('deviceSettings.tempLower')} attr={lower} min={-40} max={80} unit=" °C" />
    </SectionCard>
  );
}
